/**
 * F-131 MyWorkingHoursPage — Mon-Sun toggles with start/end time pickers.
 *
 * Renders 7 rows (Mon..Sun). Each row has a checkbox + two `<input type="time">`
 * inputs. Save → PUT replaces the whole week.
 */
import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import { Card, Button, Tooltip } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useBackButton, useMainButton } from '@/hooks';
import {
  fetchWorkingHours,
  replaceWorkingHours,
  type WorkingHoursInput,
} from '@/api/booking';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const WEEKDAYS: { idx: number; label: string }[] = [
  { idx: 0, label: 'Понеділок' },
  { idx: 1, label: 'Вівторок' },
  { idx: 2, label: 'Середа' },
  { idx: 3, label: 'Четвер' },
  { idx: 4, label: "П'ятниця" },
  { idx: 5, label: 'Субота' },
  { idx: 6, label: 'Неділя' },
];

interface RowState {
  active: boolean;
  start: string;
  end: string;
}

const DEFAULT_ROW: RowState = { active: false, start: '09:00', end: '18:00' };

export function MyWorkingHoursPage(): JSX.Element {
  useBackButton();
  const [rows, setRows] = useState<RowState[]>(() =>
    Array.from({ length: 7 }, () => ({ ...DEFAULT_ROW })),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchWorkingHours()
      .then((res) => {
        if (cancelled) return;
        const next: RowState[] = Array.from({ length: 7 }, () => ({ ...DEFAULT_ROW }));
        for (const wh of res.results) {
          if (wh.weekday < 0 || wh.weekday > 6) continue;
          next[wh.weekday] = {
            active: wh.is_active,
            start: wh.start_time.slice(0, 5),
            end: wh.end_time.slice(0, 5),
          };
        }
        setRows(next);
      })
      .catch((err) => logger.error('fetch working hours failed', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (): Promise<void> => {
    const items: WorkingHoursInput[] = rows
      .map((r, idx) => ({
        weekday: idx,
        start_time: r.start,
        end_time: r.end,
        is_active: r.active,
      }))
      .filter((it) => it.is_active);

    // Quick client-side validation: start < end.
    for (const it of items) {
      if (it.start_time >= it.end_time) {
        const lbl = WEEKDAYS[it.weekday]?.label ?? `Day ${it.weekday}`;
        toast.error(`Невалідний інтервал для ${lbl}`);
        return;
      }
    }
    try {
      await replaceWorkingHours(items);
      toast.success('Розклад збережено');
    } catch (err) {
      logger.error('save working hours failed', err);
      toast.error('Не вдалось зберегти');
    }
  };

  useMainButton({ text: 'Зберегти', onClick: save });

  return (
    <Layout
      title="Робочі години"
      headerRight={
        <Tooltip
          placement="bottom"
          content="Замовлення з'явиться як вікно в календарі"
        />
      }
    >
      <Card>
        <p className={styles.note}>
          Постав годинник на дні, коли працюєш. Слоти на 30 днів вперед
          згенеруються автоматично.
        </p>
      </Card>
      {loading ? (
        <Card>
          <p className={styles.note}>Завантаження…</p>
        </Card>
      ) : (
        WEEKDAYS.map((w) => {
          const row = rows[w.idx] ?? DEFAULT_ROW;
          return (
            <Card key={w.idx}>
              <label className={styles.formRow}>
                <input
                  type="checkbox"
                  checked={row.active}
                  onChange={(e) =>
                    setRows((prev) => {
                      const next = [...prev];
                      next[w.idx] = { ...row, active: e.target.checked };
                      return next;
                    })
                  }
                />
                <strong>{w.label}</strong>
              </label>
              {row.active && (
                <div className={styles.formRow}>
                  <input
                    type="time"
                    value={row.start}
                    onChange={(e) =>
                      setRows((prev) => {
                        const next = [...prev];
                        next[w.idx] = { ...row, start: e.target.value };
                        return next;
                      })
                    }
                  />
                  <span>—</span>
                  <input
                    type="time"
                    value={row.end}
                    onChange={(e) =>
                      setRows((prev) => {
                        const next = [...prev];
                        next[w.idx] = { ...row, end: e.target.value };
                        return next;
                      })
                    }
                  />
                </div>
              )}
            </Card>
          );
        })
      )}
      <Card>
        <Button onClick={save}>Зберегти</Button>
      </Card>
    </Layout>
  );
}
